import { createContext, useState, useEffect } from "react";
import homePageAPI from "../api/homePageApi";
import categoryAPI from "../api/categoryApi";

export const CategoryContext = createContext();

const CACHE_KEY = "categories_cache";
const CACHE_TIME = 60 * 60 * 1000; // 1 hour

// "All categories" (full list from GET /categories) — used wherever we need
// every category, not just the navbar-curated set: Shop/Product filter
// sidebar, Home page "Explore Categories" section, etc.
const ALL_CACHE_KEY = "all_categories_cache";
const ALL_CACHE_TIME = 60 * 60 * 1000; // 1 hour
const ALL_CATEGORIES_PAGE_LIMIT = 100; // items per page while paging through /categories
const ALL_CATEGORIES_MAX_PAGES = 50; // safety cap so a bad pagination response can't loop forever

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allCategoriesLoading, setAllCategoriesLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchAllCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);

        if (Date.now() - timestamp < CACHE_TIME) {
          setCategories(data);
          return;
        }
      }

      const res = await homePageAPI.fetchCategories();

      if (res.code === 200) {
        setCategories(res.data);

        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            data: res.data,
            timestamp: Date.now(),
          }),
        );
      }
    } catch (err) {
      console.log("No Data");
    }
  };

  // Fetches EVERY category (flat list, GET /categories) by walking pages
  // until pagination.totalCount is satisfied, then caches the merged result.
  const fetchAllCategories = async () => {
    setAllCategoriesLoading(true);

    try {
      const cachedData = localStorage.getItem(ALL_CACHE_KEY);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);

        if (Date.now() - timestamp < ALL_CACHE_TIME) {
          setAllCategories(data);
          setAllCategoriesLoading(false);
          return;
        }
      }

      let page = 1;
      let merged = [];
      let totalCount = 0;

      do {
        const res = await categoryAPI.listing({
          page,
          limit: ALL_CATEGORIES_PAGE_LIMIT,
        });

        if (res.code !== 200) break;

        const { items, pagination } = res.data || {};

        merged = [...merged, ...(items || [])];
        totalCount = pagination?.totalCount ?? merged.length;

        page += 1;
      } while (merged.length < totalCount && page <= ALL_CATEGORIES_MAX_PAGES);

      setAllCategories(merged);

      localStorage.setItem(
        ALL_CACHE_KEY,
        JSON.stringify({
          data: merged,
          timestamp: Date.now(),
        }),
      );
    } catch (err) {
      console.log("No Category Data");
    } finally {
      setAllCategoriesLoading(false);
    }
  };

  return (
    <CategoryContext.Provider
      value={{ categories, allCategories, allCategoriesLoading }}
    >
      {children}
    </CategoryContext.Provider>
  );
};